import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { uploadSuper, fileUrl } from "@/lib/upload";
import { PageHeader, Field, Select } from "@/components/Kit";
import { LANDING_ICONS } from "@/pages/public/Landing";
import * as Icons from "lucide-react";
import { Save, ExternalLink, ImagePlus, Loader2, Plus, Trash2 } from "lucide-react";

export default function SiteEditor() {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["sa-landing"], queryFn: async () => (await api.get("/superadmin/landing")).data });
  const [f, setF] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (data) setF(data); }, [data]);

  const save = useMutation({
    mutationFn: async () => { const { updated_at, ...body } = f; return (await api.put("/superadmin/landing", body)).data; },
    onSuccess: () => { toast.success("Main website published"); qc.invalidateQueries({ queryKey: ["sa-landing"] }); qc.invalidateQueries({ queryKey: ["pub-landing"] }); },
    onError: (e) => toast.error(apiError(e.response?.data?.detail)),
  });

  if (!f) return null;
  const upd = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const updFeat = (i, k) => (e) => setF({ ...f, features: f.features.map((x, j) => (j === i ? { ...x, [k]: e.target.value } : x)) });
  const pickHero = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try { const r = await uploadSuper(file, "landing"); setF((x) => ({ ...x, hero_image: r.url })); toast.success("Photo uploaded — click Publish to save"); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); } finally { setBusy(false); }
  };

  return (
    <div data-testid="site-editor-page">
      <PageHeader title="Main Website Editor" subtitle="Edit the public landing page — hero, school directory heading, features and footer"
        action={<a href="/" target="_blank" rel="noreferrer" className="ep-btn ep-btn-ghost" data-testid="site-view-link"><ExternalLink size={15} /> View site</a>} />
      <div className="grid gap-5 lg:grid-cols-5">
        <div className="ep-card space-y-3 p-5 lg:col-span-3">
          <h3 className="font-semibold font-head text-white">Hero section</h3>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Brand name" value={f.brand_name} onChange={upd("brand_name")} data-testid="se-brand" />
            <Field label="Brand subtitle" value={f.brand_sub} onChange={upd("brand_sub")} data-testid="se-brand-sub" />
          </div>
          <Field label="Badge text" value={f.badge} onChange={upd("badge")} data-testid="se-badge" />
          <div className="grid grid-cols-[2fr_1fr] gap-3">
            <Field label="Headline" value={f.hero_title} onChange={upd("hero_title")} data-testid="se-title" />
            <Field label="Highlighted word(s)" value={f.hero_highlight} onChange={upd("hero_highlight")} data-testid="se-highlight" />
          </div>
          <label className="block"><span className="mb-1.5 block text-xs font-medium text-slate-400">Intro text</span><textarea className="ep-input" rows={3} value={f.hero_text} onChange={upd("hero_text")} data-testid="se-text" /></label>
          <div>
            <span className="mb-1.5 block text-xs font-medium text-slate-400">Hero photo</span>
            <div className="flex items-center gap-3">
              <div className="h-24 w-20 overflow-hidden rounded-xl bg-slate-800 ring-1 ring-slate-700">{f.hero_image && <img src={fileUrl(f.hero_image)} alt="" className="h-full w-full object-cover" />}</div>
              <label className="ep-btn ep-btn-ghost cursor-pointer !py-2 text-xs" data-testid="se-hero-upload-label">{busy ? <Loader2 size={14} className="animate-spin" /> : <ImagePlus size={14} />} Upload photo<input type="file" accept="image/*" className="hidden" onChange={pickHero} data-testid="se-hero-upload" /></label>
              <input className="ep-input flex-1 !py-2 text-xs" placeholder="or paste image URL" value={f.hero_image} onChange={upd("hero_image")} data-testid="se-hero-url" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Floating card title" value={f.hero_card_title} onChange={upd("hero_card_title")} data-testid="se-card-title" />
            <Field label="Floating card text" value={f.hero_card_text} onChange={upd("hero_card_text")} data-testid="se-card-text" />
          </div>
          <h3 className="pt-2 font-semibold font-head text-white">School directory</h3>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Directory heading" value={f.directory_title} onChange={upd("directory_title")} data-testid="se-dir-title" />
            <Field label="Directory subtitle" value={f.directory_subtitle} onChange={upd("directory_subtitle")} data-testid="se-dir-sub" />
          </div>
          <h3 className="pt-2 font-semibold font-head text-white">Footer</h3>
          <Field label="Footer text" value={f.footer_text} onChange={upd("footer_text")} data-testid="se-footer" />
          <div className="grid grid-cols-2 gap-3">
            <Field label="Contact email" value={f.contact_email} onChange={upd("contact_email")} data-testid="se-email" />
            <Field label="Contact phone" value={f.contact_phone} onChange={upd("contact_phone")} data-testid="se-phone" />
          </div>
        </div>
        <div className="ep-card p-5 lg:col-span-2">
          <div className="mb-3 flex items-center justify-between">
            <h3 className="font-semibold font-head text-white">Feature cards</h3>
            <button className="ep-btn ep-btn-ghost !px-2.5 !py-1.5 text-xs" onClick={() => setF({ ...f, features: [...f.features, { icon: "Sparkles", title: "", text: "" }] })} data-testid="se-add-feature"><Plus size={13} /> Card</button>
          </div>
          <Field label="Section heading" value={f.features_title} onChange={upd("features_title")} data-testid="se-features-title" />
          <div className="mt-3 space-y-3">
            {f.features.map((x, i) => {
              const I = Icons[x.icon] || Icons.Sparkles;
              return (
                <div key={i} className="rounded-xl border border-slate-800 bg-slate-900/50 p-3" data-testid={`se-feature-${i}`}>
                  <div className="flex items-center gap-2">
                    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-indigo-500/20 text-indigo-300"><I size={15} /></div>
                    <Select label="" value={x.icon} onChange={updFeat(i, "icon")}>{LANDING_ICONS.map((ic) => <option key={ic}>{ic}</option>)}</Select>
                    <button onClick={() => setF({ ...f, features: f.features.filter((_, j) => j !== i) })} className="mt-1 text-slate-500 hover:text-rose-400"><Trash2 size={14} /></button>
                  </div>
                  <input className="ep-input mt-2 !py-1.5 text-sm" placeholder="Title" value={x.title} onChange={updFeat(i, "title")} />
                  <input className="ep-input mt-1.5 !py-1.5 text-xs" placeholder="Description" value={x.text} onChange={updFeat(i, "text")} />
                </div>
              );
            })}
          </div>
          <button className="ep-btn mt-5 w-full" disabled={save.isPending || busy} onClick={() => save.mutate()} data-testid="se-save-btn"><Save size={15} /> {save.isPending ? "Publishing…" : "Publish main website"}</button>
        </div>

        <div className="ep-card space-y-3 p-5 lg:col-span-5" data-testid="se-login-section">
          <div className="flex items-center gap-2">
            <Icons.LogIn size={16} className="text-indigo-300" />
            <h3 className="font-semibold font-head text-white">Staff sign-in page</h3>
          </div>
          <p className="-mt-1 text-xs text-slate-500">The marketing panel shown beside the login box on edupulse.aatreya.org/login</p>
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Small label" value={f.login_kicker ?? "Multi-School ERP"} onChange={upd("login_kicker")} data-testid="se-login-kicker" />
            <Field label="Headline" value={f.login_title ?? "Run every school from one command center."} onChange={upd("login_title")} data-testid="se-login-title" />
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <span className="mb-1.5 block text-xs font-medium text-slate-400">Bullet points</span>
              {(f.login_points ?? ["", "", ""]).slice(0, 4).map((p, i) => (
                <input key={i} className="ep-input mb-1.5 !py-1.5 text-sm" placeholder={`Point ${i + 1}`} value={p}
                  onChange={(e) => setF({ ...f, login_points: (f.login_points ?? ["", "", ""]).map((x, j) => (j === i ? e.target.value : x)) })}
                  data-testid={`se-login-point-${i}`} />
              ))}
            </div>
            <div>
              <span className="mb-1.5 block text-xs font-medium text-slate-400">Stat strip (value · label)</span>
              {(f.login_stats ?? [{}, {}, {}]).slice(0, 3).map((s, i) => (
                <div key={i} className="mb-1.5 grid grid-cols-2 gap-2">
                  <input className="ep-input !py-1.5 text-sm" placeholder="10K+" value={s.value || ""}
                    onChange={(e) => setF({ ...f, login_stats: (f.login_stats ?? [{}, {}, {}]).map((x, j) => (j === i ? { ...x, value: e.target.value } : x)) })}
                    data-testid={`se-login-stat-value-${i}`} />
                  <input className="ep-input !py-1.5 text-sm" placeholder="Students" value={s.label || ""}
                    onChange={(e) => setF({ ...f, login_stats: (f.login_stats ?? [{}, {}, {}]).map((x, j) => (j === i ? { ...x, label: e.target.value } : x)) })}
                    data-testid={`se-login-stat-label-${i}`} />
                </div>
              ))}
            </div>
          </div>
          <button className="ep-btn w-full sm:w-auto" disabled={save.isPending || busy} onClick={() => save.mutate()} data-testid="se-login-save-btn"><Save size={15} /> {save.isPending ? "Publishing…" : "Publish"}</button>
        </div>
      </div>
    </div>
  );
}
